/* KOI signup page.
   - Background: a topographic contour field (the bag / soleplate motif) drawn in
     WebGL. The pointer presses a hill into the terrain so the contour lines bend
     around the ball and settle back behind it; a click "kicks" a ripple out.
     Every 5th line is an index contour in lime, the rest cobalt, over a slow
     gold / ember glow.
   - Cursor: a soccer ball that follows the pointer and rolls with its speed.
     Desktop (fine pointer) only; touch screens get drifting ghost touches.
   - Game (experience "game"): a 5-a-side match plays over the field; the
     cursor is your player and the ball's position also dents the terrain.
   - Scramble: headings resolve from random glyphs on load.
   - Form: posts straight to Klaviyo's client subscriptions API.
   Reduced motion: one still frame, native cursor, no scramble. */
(() => {
  const root = document.querySelector("[data-koi]");
  if (!root) return;

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  // Shared pointer state, in CSS pixels. Read by the background and the ball.
  const pointer = { x: innerWidth / 2, y: innerHeight / 2, active: false };
  const kicks = []; // { x, y, t } click ripples, newest last
  const field = { x: 0, y: 0, on: 0 }; // game ball in screen px, for the terrain

  /* ------------------------------------------------------------ background */

  const TRAIL = 24; // pointer history points that press the terrain
  const KICKS = 4;
  const MAX_DPR = 1.5;

  const VERT = `
attribute vec2 p;
void main() { gl_Position = vec4(p, 0.0, 1.0); }`;

  const FRAG = `
#extension GL_OES_standard_derivatives : enable
precision highp float;
uniform vec2 uRes;
uniform float uT;
uniform float uDpr;
uniform vec3 uTrail[${TRAIL}];  // x, y (px), strength 0..1
uniform vec3 uPtr;              // smoothed pointer x, y (px), presence 0..1
uniform vec2 uPar;              // smoothed pointer offset from centre, -0.5..0.5
uniform vec4 uKick[${KICKS}];   // x, y (px), age (s), on
uniform vec3 uGame;             // game ball x, y (px), on

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1, 0)), u.x),
             mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), u.x), u.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) { v += a * noise(p); p = p * 2.02 + 3.1; a *= 0.5; }
  return v;
}

void main() {
  vec2 px = gl_FragCoord.xy / uDpr;
  px.y = uRes.y - px.y;                         // match DOM coordinates
  float unit = min(uRes.x, uRes.y);
  vec2 q = px / unit;

  // Lens: the map magnifies under the ball, like leaning in over it
  vec2 pq = uPtr.xy / unit;
  float pd = length(q - pq);
  float lens = uPtr.z * exp(-pd * pd / 0.035);
  q = pq + (q - pq) * (1.0 - 0.45 * lens);

  // The whole terrain leans toward the pointer (parallax)
  vec2 tq = q + uPar * 0.25;

  // Terrain: slow-breathing fbm hills
  float h = fbm(tq * 2.2 + vec2(uT * 0.015, -uT * 0.01));
  h += 0.35 * fbm(tq * 1.1 - uT * 0.02);

  // The ball presses a hill into the terrain; the trail decays behind it
  float press = 0.0;
  for (int i = 0; i < ${TRAIL}; i++) {
    vec3 tr = uTrail[i];
    float d = length(px - tr.xy) / unit;
    press += tr.z * exp(-d * d / 0.012);
  }
  h += 0.5 * (1.0 - exp(-1.4 * press));   // saturates: a still ball makes a dome, not a stack
  float near = 1.0 - exp(-1.5 * press);   // heat from the whole trail, not its strongest sample, so it never flickers

  // the game ball dimples the terrain as it rolls
  float gd = length(px - uGame.xy) / unit;
  float gb = uGame.z * exp(-gd * gd / 0.002);
  h += 0.22 * gb;
  near = max(near, 0.8 * gb);

  // Kicks: rings that run out from the click and fade
  float ring = 0.0;
  for (int i = 0; i < ${KICKS}; i++) {
    vec4 k = uKick[i];
    if (k.w > 0.5) {
      float d = length(px - k.xy) / unit;
      float r = k.z * 0.55;
      float env = exp(-k.z * 1.6) * smoothstep(0.0, 0.08, k.z);
      float w = exp(-pow((d - r) / 0.05, 2.0));
      h += 0.18 * w * env * sin((d - r) * 60.0);
      ring += w * env;
    }
  }

  // Contours with screen-space constant width
  float f = h * 14.0;
  float fw = fwidth(f);
  float g = abs(fract(f - 0.5) - 0.5) / max(fw, 1e-4);
  float line = 1.0 - smoothstep(0.6, 1.6, g);
  float idx = mod(floor(f + 0.5), 5.0) < 0.5 ? 1.0 : 0.0;   // index contour

  // Stage: near-black with a slow gold / ember glow, like a studio key light
  vec3 ink = vec3(0.039, 0.039, 0.043);
  vec3 gold = vec3(0.96, 0.706, 0.09);
  vec3 ember = vec3(0.886, 0.314, 0.122);
  vec3 cobalt = vec3(0.137, 0.275, 1.0);
  vec3 lime = vec3(0.843, 1.0, 0.169);

  float glow = fbm(q * 0.9 - uPar * 0.4 + vec2(uT * 0.02, uT * 0.013));
  vec3 col = ink + mix(gold, ember, smoothstep(0.35, 0.7, glow)) * 0.10 * smoothstep(0.25, 0.8, glow);
  float spot = uPtr.z * exp(-pd * pd / 0.09);                 // key light follows the ball
  col += gold * (0.07 * spot + 0.14 * near);

  vec3 lc = mix(cobalt, lime, idx);
  lc = mix(lc, mix(gold, ember, near), clamp(near * 1.2 + lens * 0.4, 0.0, 0.85));   // lines heat up near the ball
  float la = mix(0.3, 0.5, idx) + 0.35 * spot + 0.5 * near + 0.6 * ring;
  col = mix(col, lc, clamp(line * la, 0.0, 1.0));

  // grain, also kills gradient banding
  col += (hash(gl_FragCoord.xy + fract(uT) * 100.0) - 0.5) * 0.025;

  // vignette
  vec2 v = gl_FragCoord.xy / (uRes * uDpr) - 0.5;
  col *= 1.0 - 0.6 * dot(v, v);

  gl_FragColor = vec4(col, 1.0);
}`;

  function startBackground() {
    const canvas = root.querySelector("[data-koi-bg]");
    const gl = canvas.getContext("webgl", { antialias: false, alpha: false, powerPreference: "high-performance" });
    if (!gl || !gl.getExtension("OES_standard_derivatives")) return null; // CSS gradient fallback stays

    const compile = (type, src) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
      return s;
    };
    const prog = gl.createProgram();
    try {
      gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
      gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG));
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(prog));
    } catch (err) {
      console.warn("[koi] background disabled:", err);
      return null;
    }
    gl.useProgram(prog);

    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "p");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    const u = (n) => gl.getUniformLocation(prog, n);
    const uRes = u("uRes"), uT = u("uT"), uDpr = u("uDpr"), uTrail = u("uTrail"), uKick = u("uKick");
    const uPtr = u("uPtr"), uPar = u("uPar"), uGame = u("uGame");
    const smooth = { x: innerWidth / 2, y: innerHeight / 2, on: 0 };

    let dpr = 1;
    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
      const w = canvas.clientWidth, h = canvas.clientHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(uRes, w, h);
      gl.uniform1f(uDpr, dpr);
    };
    resize();

    const trail = Array.from({ length: TRAIL }, () => ({ x: 0, y: 0, t: -1e9 }));
    const trailBuf = new Float32Array(TRAIL * 3);
    const kickBuf = new Float32Array(KICKS * 4);
    let head = 0, lastSample = 0;

    // Touch screens: invisible "ghost touches" wander so the field still moves
    const ghost = { x: innerWidth * 0.3, y: innerHeight * 0.4 };

    const t0 = performance.now();
    const draw = (now) => {
      const t = (now - t0) / 1000;

      let px = pointer.x, py = pointer.y, on = pointer.active;
      if (!finePointer && field.on) {
        px = field.x; py = field.y; on = true; // touch screens: the game ball leads the terrain
      } else if (!finePointer && !reduceMotion) {
        ghost.x = innerWidth * (0.5 + 0.38 * Math.sin(t * 0.21) * Math.cos(t * 0.13));
        ghost.y = innerHeight * (0.5 + 0.35 * Math.sin(t * 0.17 + 1.3));
        px = ghost.x; py = ghost.y; on = true;
      }

      // sample the pointer into a ring buffer; samples fade with age
      if (now - lastSample > 30) {
        lastSample = now;
        head = (head + 1) % TRAIL;
        trail[head].x = px; trail[head].y = py; trail[head].t = on ? now : -1e9;
      }
      // Each sample fades in, then out to exactly zero before the ring buffer
      // reuses it, so a resting pointer gives a steady dome instead of a flicker.
      for (let i = 0; i < TRAIL; i++) {
        const p = trail[i], age = (now - p.t) / 1000;
        const w = Math.min(age / 0.06, 1) * Math.max(0, 1 - age / 0.7) ** 2;
        trailBuf[i * 3] = p.x; trailBuf[i * 3 + 1] = p.y; trailBuf[i * 3 + 2] = w;
      }

      while (kicks.length > KICKS) kicks.shift();
      kickBuf.fill(0);
      kicks.forEach((k, i) => {
        const age = (now - k.t) / 1000;
        if (age < 3) kickBuf.set([k.x, k.y, age, 1], i * 4);
      });

      smooth.x += (px - smooth.x) * 0.08;
      smooth.y += (py - smooth.y) * 0.08;
      smooth.on += ((on ? 1 : 0) - smooth.on) * 0.05;
      gl.uniform3f(uPtr, smooth.x, smooth.y, smooth.on);
      gl.uniform2f(uPar, smooth.x / innerWidth - 0.5, smooth.y / innerHeight - 0.5);

      gl.uniform3f(uGame, field.x, field.y, field.on);
      gl.uniform1f(uT, reduceMotion ? 12 : t);
      gl.uniform3fv(uTrail, trailBuf);
      gl.uniform4fv(uKick, kickBuf);
      gl.drawArrays(gl.TRIANGLES, 0, 3);

      if (!reduceMotion) requestAnimationFrame(draw);
    };
    requestAnimationFrame(draw);
    addEventListener("resize", () => {
      resize();
      if (reduceMotion) requestAnimationFrame(draw); // the still frame has no loop to repaint it
    });
  }

  /* ------------------------------------------------------------- the ball */

  function startBall() {
    const ball = root.querySelector("[data-koi-ball]");
    const spin = ball.querySelector("[data-koi-ball-spin]");
    if (!finePointer || reduceMotion) { ball.remove(); return; }

    root.classList.add("koi--ball");
    let x = pointer.x, y = pointer.y, angle = 0, squash = 0, kick = 0;
    let lastX = x;

    const loop = () => {
      x += (pointer.x - x) * 0.35;
      y += (pointer.y - y) * 0.35;
      const vx = x - lastX;
      lastX = x;
      angle += vx * 2.6; // roll: ~ distance / radius in degrees
      squash += (Math.min(Math.abs(vx) / 40, 0.12) - squash) * 0.2;
      kick *= 0.88;
      const s = 1 + kick * 0.5;
      ball.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${s * (1 + squash)}, ${s * (1 - squash)})`;
      spin?.setAttribute("transform", `rotate(${angle % 360})`); // the game's ring marker doesn't roll
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);

    root.addEventListener("pointerdown", () => { kick = 1; });

    // Real cursor over controls: the caret / pointer is what people need there
    const controls = "input, select, textarea, button, label";
    root.addEventListener("pointerover", (e) => {
      ball.classList.toggle("is-small", !!e.target.closest(controls));
    });
    document.addEventListener("pointerleave", () => { ball.style.opacity = "0"; });
    document.addEventListener("pointerenter", () => { ball.style.opacity = ""; });
  }

  /* ---------------------------------------------------------------- game */

  // Mini 5-a-side over the contour field (experience: "game").
  // Pitch coordinates: u runs goal to goal, v across. Landscape screens play
  // left-right, portrait tablets top-bottom (a transpose), so the goals sit in
  // open space. Phones, where the form fills the screen, get a 3-a-side in an
  // open strip above the headline instead.
  const SKIN = ["#3b2414", "#5c3a21", "#8d5524", "#a86b3c", "#c68642", "#e0ac69", "#f1c27d"];
  const KITS = [
    { shirt: "#f5b417", trim: "#0a0a0b", shorts: "#0a0a0b", sock: "#f5b417", keeper: "#e2501f", gloves: "#d7ff2b" }, // Gato (gold), attacks +u
    { shirt: "#2346ff", trim: "#d7ff2b", shorts: "#0a0a0b", sock: "#d7ff2b", keeper: "#d7ff2b", gloves: "#e2501f" }, // Maestro (cobalt), attacks -u
  ];
  const HAIR = ["short", "afro", "buzz", "bun", "curls"];
  const HAIR_COLORS = ["#140c08", "#140c08", "#2a1a10", "#140c08", "#5a3418"];
  // home spots for a team attacking +u, as fractions of the pitch; [0] is the keeper
  const FORMATION = [[0.02, 0.5], [0.22, 0.3], [0.22, 0.7], [0.4, 0.5], [0.58, 0.4]];

  function startGame() {
    const canvas = root.querySelector("[data-koi-game]");
    if (!canvas) return;
    const hud = root.querySelector("[data-koi-hud]");
    if (reduceMotion) { canvas.remove(); hud.remove(); return; }

    const ctx = canvas.getContext("2d");
    const scoreEls = hud.querySelectorAll("[data-koi-score]");
    const hint = hud.querySelector("[data-koi-hint]");
    hint.textContent = finePointer ? "RUN INTO THE BALL TO DRIBBLE · CLICK TO SHOOT" : "TAP TO PASS THE BALL";

    const blockers = [...root.querySelectorAll(".koi-head .koi-tag, .koi-panel")];
    const score = [0, 0];
    const ball = { u: 0, v: 0, vu: 0, vv: 0, z: 0, vz: 0, spin: 0, r: 5, live: false }; // z: height off the grass
    const cursor = { u: 0, v: 0, vu: 0, vv: 0 };
    let W = 0, H = 0, L = 0, B = 0, s = 1, dpr = 1, gh = 0, pr = 7;
    let vertical = false, strip = false, ox = 0, oy = 0, lastKick = 0, owner = -1, flash = null, played = false, still = 0;
    const chasers = [null, null];

    const players = [];
    KITS.forEach((kit, team) => FORMATION.forEach((home, i) => players.push({
      team, home, keeper: i === 0, u: 0, v: 0, vu: 0, vv: 0, dir: team ? -1 : 1,
      phase: Math.random() * 6, cool: 0, kickT: -1e9, tu: null, tv: null, turn: 0, face: team ? -1 : 1, skin: SKIN[(team * 3 + i * 2) % SKIN.length],
      hair: HAIR[(team * 2 + i) % HAIR.length], hairColor: HAIR_COLORS[(team + i * 3) % HAIR_COLORS.length],
    })));

    const toPitch = (x, y) => (vertical ? [y, x] : [x - ox, y - oy]);
    const toScreen = (u, v) => (vertical ? [v, u] : [u + ox, v + oy]);
    const playing = (p) => !(strip && (p.home === FORMATION[1] || p.home === FORMATION[2])); // phones drop the back two

    const homeOf = (p, bu, bv) => {
      const mirror = (u) => (p.team ? L - u : u);
      let hu = p.home[0], hv = p.home[1];
      if (!p.keeper) {
        hu += (mirror(bu) / L - 0.5) * 0.35; // the shape slides with the ball
        hv += (bv / B - 0.5) * 0.3;
      }
      return [mirror(hu * L), hv * B];
    };

    const rects = () => (strip ? [] : blockers).filter((el) => el.offsetParent).map((el) => {
      const r = el.getBoundingClientRect();
      const [u0, v0] = toPitch(r.left, r.top), [u1, v1] = toPitch(r.right, r.bottom);
      return { u0, v0, u1, v1 };
    });

    const freeSpot = (rs) => {
      for (const [fu, fv] of [[0.5, 0.5], [0.5, 0.12], [0.5, 0.88], [0.32, 0.5], [0.68, 0.5]]) {
        const u = fu * L, v = fv * B, m = 24 * s;
        if (!rs.some((r) => u > r.u0 - m && u < r.u1 + m && v > r.v0 - m && v < r.v1 + m)) return [u, v];
      }
      return [L * 0.3, B * 0.1];
    };

    const kickoff = () => {
      [ball.u, ball.v] = freeSpot(rects());
      ball.vu = ball.vv = ball.z = ball.vz = 0;
      ball.live = true;
    };

    const resize = () => {
      // Phones fire resize when the toolbar slides or the layout shifts; only a
      // real change of pitch shape restarts play, otherwise just fit the canvas.
      const rebuild = innerWidth !== W || (!strip && innerHeight !== H);
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      W = innerWidth; H = innerHeight;
      canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
      canvas.style.width = `${W}px`; canvas.style.height = `${H}px`;
      if (!rebuild) return;
      strip = W < 700;
      vertical = !strip && H > W;
      if (strip) {
        ox = 0; oy = 44;
        L = W; B = Math.round(Math.max(150, Math.min(200, H * 0.22)));
        root.style.setProperty("--koi-pitch-h", `${oy + B + 16}px`); // headline starts below the strip
      } else {
        ox = oy = 0;
        [L, B] = vertical ? [H, W] : [W, H];
        root.style.removeProperty("--koi-pitch-h");
      }
      s = Math.min(Math.max(Math.min(W, H) / 800, 0.75), 1.25);
      pr = 8 * s; // footprint for tackles and spacing; the sprite stands above it
      ball.r = 5 * s;
      gh = strip ? B * 0.5 : Math.min(B * 0.34, 240 * s);
      players.forEach((p) => { [p.u, p.v] = homeOf(p, L / 2, B / 2); p.vu = p.vv = 0; p.tu = p.tv = null; });
      kickoff();
    };

    // Move a spot out from behind the form / headline so players stay in view
    const inView = ([u, v], rs) => {
      const m = pr * 1.6;
      for (const r of rs) {
        if (u <= r.u0 - m || u >= r.u1 + m || v <= r.v0 - m || v >= r.v1 + m) continue;
        const pen = [u - (r.u0 - m), r.u1 + m - u, v - (r.v0 - m), r.v1 + m - v];
        const i = pen.indexOf(Math.min(...pen));
        if (i === 0) u = r.u0 - m; else if (i === 1) u = r.u1 + m; else if (i === 2) v = r.v0 - m; else v = r.v1 + m;
      }
      return [u, v];
    };

    const kickBall = (du, dv, speed) => {
      const d = Math.hypot(du, dv) || 1;
      ball.vu = (du / d) * speed;
      ball.vv = (dv / d) * speed;
      ball.vz = speed > 420 * s ? speed * 0.28 : 0; // shots and long balls get lifted
      lastKick = performance.now();
      owner = -1; // the cursor and nudges belong to nobody; decide() reclaims it
    };

    const interacted = () => {
      if (played) return;
      played = true;
      hint.classList.add("is-hidden");
    };

    // What a player does with the ball at their feet
    const decide = (p) => {
      const dir = p.team ? -1 : 1;
      const goalU = p.team ? 0 : L;
      if (p.keeper) {
        kickBall(dir * L * 0.5, (Math.random() - 0.5) * B, 470 * s);
        return;
      }
      if (Math.abs(goalU - ball.u) < L * (strip ? 0.46 : 0.3)) {
        const aim = B / 2 + (Math.random() - 0.5) * gh * 1.15; // some go wide
        kickBall(goalU - ball.u, aim - ball.v, (480 + Math.random() * 180) * s);
        return;
      }
      const mates = players.filter((m) => m.team === p.team && m !== p && !m.keeper && playing(m) && (m.u - p.u) * dir > 24 * s);
      if (mates.length && Math.random() < 0.55) {
        const m = mates[(Math.random() * mates.length) | 0];
        const du = m.u + m.vu * 0.35 - ball.u, dv = m.v + m.vv * 0.35 - ball.v;
        kickBall(du, dv, Math.min(Math.hypot(du, dv) * 1.3 + 110 * s, 520 * s));
        return;
      }
      kickBall(dir * 100 * s, (B / 2 - ball.v) * 0.25 + (Math.random() - 0.5) * 70 * s, (140 + Math.random() * 50) * s);
    };

    const goal = (side, now) => {
      const team = side ? 0 : 1; // the far goal belongs to cobalt, so gold scores there
      score[team]++;
      scoreEls[team].textContent = score[team];
      ball.live = false;
      flash = { side, t: now };
      const [x, y] = toScreen(side ? L : 0, B / 2);
      kicks.push({ x, y, t: now });
      setTimeout(kickoff, 1700);
    };

    // The form and headline are walls the ball bounces off
    const collide = (r) => {
      const u0 = r.u0 - ball.r, u1 = r.u1 + ball.r, v0 = r.v0 - ball.r, v1 = r.v1 + ball.r;
      if (ball.u <= u0 || ball.u >= u1 || ball.v <= v0 || ball.v >= v1) return;
      const pen = [ball.u - u0, u1 - ball.u, ball.v - v0, v1 - ball.v];
      const i = pen.indexOf(Math.min(...pen));
      if (i === 0) { ball.u = u0; ball.vu = -Math.abs(ball.vu) * 0.75; }
      else if (i === 1) { ball.u = u1; ball.vu = Math.abs(ball.vu) * 0.75; }
      else if (i === 2) { ball.v = v0; ball.vv = -Math.abs(ball.vv) * 0.75; }
      else { ball.v = v1; ball.vv = Math.abs(ball.vv) * 0.75; }
    };

    const step = (dt, now) => {
      // the visitor: running into the ball dribbles it
      const [cu, cv] = toPitch(pointer.x, pointer.y);
      cursor.vu = (cu - cursor.u) / dt; cursor.vv = (cv - cursor.v) / dt;
      cursor.u = cu; cursor.v = cv;
      if (finePointer && pointer.active && ball.live) {
        const du = ball.u - cu, dv = ball.v - cv, d = Math.hypot(du, dv) || 1, touch = ball.r + 14 * s;
        if (d < touch) {
          kickBall(du, dv, Math.min(Math.max(Math.hypot(cursor.vu, cursor.vv) * 1.1, 170 * s), 900 * s));
          ball.u = cu + (du / d) * touch; ball.v = cv + (dv / d) * touch;
          interacted();
        }
      }

      const rs = rects();
      if (ball.live) {
        const f = Math.exp(-1.1 * dt);
        ball.vu *= f; ball.vv *= f;
        ball.u += ball.vu * dt; ball.v += ball.vv * dt;
        ball.spin += (Math.hypot(ball.vu, ball.vv) * dt) / ball.r;
        if (ball.z > 0 || ball.vz > 0) {
          ball.vz -= 900 * s * dt;
          ball.z += ball.vz * dt;
          if (ball.z <= 0) { ball.z = 0; ball.vz = ball.vz < -60 * s ? -ball.vz * 0.45 : 0; } // bounce, then settle
        }
        if (ball.v < ball.r) { ball.v = ball.r; ball.vv = Math.abs(ball.vv) * 0.7; }
        if (ball.v > B - ball.r) { ball.v = B - ball.r; ball.vv = -Math.abs(ball.vv) * 0.7; }
        if (ball.u < ball.r || ball.u > L - ball.r) {
          const side = ball.u < ball.r ? 0 : 1;
          if (Math.abs(ball.v - B / 2) < gh / 2 - ball.r) goal(side, now);
          else { ball.u = side ? L - ball.r : ball.r; ball.vu = -ball.vu * 0.7; }
        }
        rs.forEach(collide);

        // a ball nobody can get to (players boxing each other out) gets a nudge
        still = Math.hypot(ball.vu, ball.vv) < 5 * s ? still + dt : 0;
        if (still > 2.5) { kickBall(Math.random() - 0.5, Math.random() - 0.5, 220 * s); still = 0; }
      }

      // the outfield player nearest the ball chases it (sticky, so it doesn't flicker)
      for (const team of [0, 1]) {
        let best = null, bd = Infinity;
        for (const p of players) {
          if (p.team !== team || p.keeper || !playing(p)) continue;
          const d = Math.hypot(p.u - ball.u, p.v - ball.v) * (p === chasers[team] ? 0.8 : 1);
          if (d < bd) { bd = d; best = p; }
        }
        chasers[team] = best;
      }

      const reach = pr + ball.r + 3 * s;
      for (const p of players) {
        if (!playing(p)) continue;
        const dir = p.team ? -1 : 1;
        let tu, tv, max = 72 * s;
        if (!ball.live) {
          [tu, tv] = homeOf(p, L / 2, B / 2);
        } else if (p.keeper) {
          tu = p.team ? L - 16 * s : 16 * s;
          // cover the angle, but not perfectly, so shots can beat them
          const cover = gh / 2 - pr;
          tv = B / 2 + Math.max(-cover, Math.min(cover, (ball.v - B / 2) * 0.7));
          if (Math.abs(ball.u - tu) < L * 0.1) { tu = ball.u; tv = ball.v; } // come out for it
          max = 85 * s;
        } else if (p === chasers[p.team]) {
          // get on the right side of the ball, then run onto it; up close, just tackle
          const close = Math.hypot(ball.u - p.u, ball.v - p.v) < reach * 2.2;
          const pastIt = !close && (ball.u - p.u) * dir < 0;
          tu = ball.u - dir * (pastIt ? reach * 1.6 : 0);
          tv = ball.v + (pastIt ? (p.v < ball.v ? -reach : reach) : 0);
          max = 108 * s;
        } else {
          [tu, tv] = homeOf(p, ball.u, ball.v);
        }
        if (!p.keeper) [tu, tv] = inView([tu, tv], rs);

        // targets drift rather than jump (chaser swaps, ball deflections), like a player reading the play
        if (p.tu === null) { p.tu = tu; p.tv = tv; }
        const ease = Math.min(1, dt * (p === chasers[p.team] ? 4 : 1.6));
        p.tu += (tu - p.tu) * ease; p.tv += (tv - p.tv) * ease;

        // steer with limited acceleration and ease off on arrival, so nobody starts, stops or turns on a dime
        const du = p.tu - p.u, dv = p.tv - p.v, d = Math.hypot(du, dv);
        const want = d > 4 * s ? Math.min(max, Math.sqrt(2 * 140 * s * d)) : 0;
        let au = (d ? du / d : 0) * want - p.vu, av = (d ? dv / d : 0) * want - p.vv;
        const a = Math.hypot(au, av), amax = 190 * s * dt;
        if (a > amax) { au *= amax / a; av *= amax / a; }
        p.vu += au; p.vv += av;

        p.cool -= dt;
        if (ball.live && p.cool <= 0 && now - lastKick > 320 && Math.hypot(p.u - ball.u, p.v - ball.v) < reach) {
          p.cool = 0.8;
          // a challenge on a ball the other team just played doesn't always come off
          if (owner >= 0 && owner !== p.team && now - lastKick < 900 && Math.random() < 0.55) continue;
          p.kickT = now;
          decide(p);
          owner = p.team;
        }
      }

      // keep players from stacking on top of each other
      for (let i = 0; i < players.length; i++) {
        for (let j = i + 1; j < players.length; j++) {
          const a = players[i], b = players[j];
          if (!playing(a) || !playing(b)) continue;
          const du = b.u - a.u, dv = b.v - a.v, d = Math.hypot(du, dv) || 1, min = pr * 2.4;
          if (d < min) {
            const push = Math.min((min - d) / 2, 30 * s * dt);
            a.u -= (du / d) * push; a.v -= (dv / d) * push;
            b.u += (du / d) * push; b.v += (dv / d) * push;
          }
        }
      }

      const head = 40 * s; // sprites stand up from their feet, so leave room above
      for (const p of players.filter(playing)) {
        p.u = Math.max(vertical ? head : pr, Math.min(L - pr, p.u + p.vu * dt));
        p.v = Math.max(vertical ? pr : head, Math.min(B - pr, p.v + p.vv * dt));
        const speed = Math.hypot(p.vu, p.vv);
        // face the way they run across the screen, else look at the ball
        const sx = vertical ? p.vv : p.vu;
        p.turn -= dt;
        let want = p.dir;
        if (Math.abs(sx) > 22 * s) want = Math.sign(sx);
        else if (speed < 8 * s) want = Math.sign((vertical ? ball.v - p.v : ball.u - p.u) || p.dir);
        if (want !== p.dir && p.turn <= 0) { p.dir = want; p.turn = 0.45; }
        p.face += (p.dir - p.face) * Math.min(1, dt * 9); // quick turn instead of an instant mirror
        p.phase += (speed * dt) / (5.2 * s);
      }

      [field.x, field.y] = toScreen(ball.u, ball.v);
      field.on = ball.live ? 1 : 0;
    };

    /* insole lockups, painted in each goal mouth and only lit where the cursor hovers */

    // Built once at a large design size, then scaled to the box at draw time.
    const lockup = (h, paint) => {
      const c = document.createElement("canvas");
      const g = c.getContext("2d");
      const w = paint(g, false); // first pass measures
      c.width = Math.ceil(w); c.height = h;
      paint(g, true);
      return c;
    };
    const setFont = (g, font, stretch = "normal", spacing = "0px") => {
      g.font = font; g.fontStretch = stretch; g.letterSpacing = spacing;
    };
    const lockups = []; // [0] Gato's goal (u = 0), [1] Maestro's goal (u = L)
    const buildLockups = () => {
      lockups[0] = lockup(400, (g, go) => {
        setFont(g, "900 300px Archivo, sans-serif", "expanded");
        const word = g.measureText("GATO").width;
        setFont(g, "700 54px 'Space Mono', monospace", "normal", "10px");
        const w = Math.max(word, g.measureText("KIDS OF IMMIGRANTS").width) + 40;
        if (!go) return w;
        g.textBaseline = "alphabetic";
        setFont(g, "900 300px Archivo, sans-serif", "expanded");
        g.fillStyle = "rgba(226, 80, 31, 0.55)";
        g.fillText("GATO", 20, 270);
        g.strokeStyle = "#d7ff2b"; g.lineWidth = 9; g.lineJoin = "round";
        g.strokeText("GATO", 20, 270);
        setFont(g, "700 54px 'Space Mono', monospace", "normal", "10px");
        g.fillStyle = "#d7ff2b";
        g.fillText("KIDS OF IMMIGRANTS", 24, 360);
        return w;
      });
      lockups[1] = lockup(430, (g, go) => {
        setFont(g, "900 230px Archivo, sans-serif", "expanded");
        const word = g.measureText("TIEMPO").width;
        const w = word + 40;
        if (!go) return w;
        // the Tiempo insole's fade: cobalt heel to ember to lime toe
        const fill = g.createLinearGradient(0, 0, w, 0);
        fill.addColorStop(0, "#7d93ff"); fill.addColorStop(0.5, "#ff6a33"); fill.addColorStop(1, "#d7ff2b");
        g.fillStyle = fill;
        g.textBaseline = "alphabetic";
        setFont(g, "700 46px 'Space Mono', monospace", "normal", "8px");
        g.fillText("KIDS OF IMMIGRANTS", 24, 70);
        setFont(g, "900 230px Archivo, sans-serif", "expanded");
        g.fillText("TIEMPO", 20, 280);
        setFont(g, "150px Yellowtail, cursive");
        g.textAlign = "right";
        g.fillText("Maestro", w - 20, 400);
        return w;
      });
    };
    buildLockups();
    if (document.fonts) {
      Promise.all(["900 100px Archivo", "700 40px 'Space Mono'", "100px Yellowtail"].map((f) => document.fonts.load(f)))
        .then(buildLockups, () => {});
    }

    const scratch = document.createElement("canvas");
    const sctx = scratch.getContext("2d");
    const drawLockups = (now) => {
      const spots = [];
      if (finePointer && pointer.active) spots.push([pointer.x, pointer.y, 170 * s, 1]);
      if (ball.live) spots.push([...toScreen(ball.u, ball.v), 60 * s, 0.6]); // the ball lights it up too (and on touch screens)
      const depth = 12 * s, box = L * 0.13;
      lockups.forEach((lk, side) => {
        const full = flash && flash.side === side ? Math.max(0, 1 - (now - flash.t) / 1600) : 0; // a goal lights its net's words
        const scale = 0.85 * Math.min((gh * 2) / lk.width, (box - depth - 8 * s) / lk.height);
        const w = lk.width * scale, h = lk.height * scale;
        const [cx, cy] = toScreen(side ? L - (box + depth) / 2 : (box + depth) / 2, B / 2);
        const bw = vertical ? w : h, bh = vertical ? h : w; // reads along the goal line, like on the insole
        const bx = cx - bw / 2, by = cy - bh / 2;
        const lit = spots.filter(([x, y, r]) => x > bx - r && x < bx + bw + r && y > by - r && y < by + bh + r);
        if (!lit.length && !full) return;

        scratch.width = Math.ceil(bw * dpr); scratch.height = Math.ceil(bh * dpr);
        sctx.setTransform(dpr, 0, 0, dpr, -bx * dpr, -by * dpr);
        if (full) { sctx.globalAlpha = full; sctx.fillRect(bx, by, bw, bh); sctx.globalAlpha = 1; }
        for (const [x, y, r, a] of lit) {
          const g = sctx.createRadialGradient(x, y, 0, x, y, r);
          g.addColorStop(0, `rgba(0,0,0,${a})`); g.addColorStop(0.55, `rgba(0,0,0,${a})`); g.addColorStop(1, "rgba(0,0,0,0)");
          sctx.fillStyle = g;
          sctx.fillRect(x - r, y - r, r * 2, r * 2);
        }
        sctx.globalCompositeOperation = "source-in";
        sctx.translate(cx, cy);
        if (!vertical) sctx.rotate(side ? Math.PI / 2 : -Math.PI / 2);
        sctx.drawImage(lk, -w / 2, -h / 2, w, h);
        sctx.globalCompositeOperation = "source-over";
        ctx.drawImage(scratch, bx, by, bw, bh);
      });
    };

    /* drawing: pitch markings in pitch space */

    const drawPitch = () => {
      ctx.strokeStyle = "rgba(215, 255, 43, 0.16)";
      ctx.lineWidth = 1.5 * s;
      ctx.beginPath();
      ctx.moveTo(L / 2, 0); ctx.lineTo(L / 2, B);
      ctx.moveTo(L / 2 + Math.min(L, B) * 0.12, B / 2);
      ctx.arc(L / 2, B / 2, Math.min(L, B) * 0.12, 0, Math.PI * 2);
      for (const side of [0, 1]) {
        const x = side ? L : 0, sgn = side ? -1 : 1;
        ctx.rect(x, B / 2 - gh * 1.15, sgn * L * 0.13, gh * 2.3);
        ctx.rect(x, B / 2 - gh * 0.7, sgn * L * 0.05, gh * 1.4);
      }
      ctx.stroke();

      // goals: a net across the mouth and white posts
      for (const side of [0, 1]) {
        const depth = 12 * s, x = side ? L - depth : 0, y = B / 2 - gh / 2;
        ctx.fillStyle = "rgba(244, 244, 241, 0.08)";
        ctx.fillRect(x, y, depth, gh);
        ctx.strokeStyle = "rgba(244, 244, 241, 0.28)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (let v = y; v <= y + gh; v += 6 * s) { ctx.moveTo(x, v); ctx.lineTo(x + depth, v); }
        for (let u = x; u <= x + depth; u += 6 * s) { ctx.moveTo(u, y); ctx.lineTo(u, y + gh); }
        ctx.stroke();
        ctx.strokeStyle = "#f4f4f1";
        ctx.lineWidth = 3 * s;
        ctx.beginPath();
        const line = side ? L - depth : depth;
        ctx.moveTo(line, y); ctx.lineTo(side ? L : 0, y);
        ctx.moveTo(line, y + gh); ctx.lineTo(side ? L : 0, y + gh);
        ctx.moveTo(line, y); ctx.lineTo(line, y + gh);
        ctx.stroke();
      }
    };

    /* players and ball are drawn upright in screen space, side-on like a
       classic football game: (x, y) is where the feet touch the grass */

    const limb = (pts, color, width, shade) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.moveTo(pts[0], pts[1]);
      for (let i = 2; i < pts.length; i += 2) ctx.lineTo(pts[i], pts[i + 1]);
      ctx.stroke();
      if (shade) { ctx.strokeStyle = `rgba(0, 0, 0, ${shade})`; ctx.stroke(); } // far-side limbs sit in shadow
    };

    const drawHair = (p, hx, hy, r) => {
      ctx.fillStyle = p.hairColor;
      ctx.beginPath();
      if (p.hair === "afro") {
        ctx.arc(hx - r * 0.25, hy - r * 0.35, r * 1.45, 0, Math.PI * 2);
      } else if (p.hair === "curls") {
        for (const [dx, dy] of [[-0.7, -0.75], [0, -1], [0.65, -0.8], [-1, -0.1]]) {
          ctx.moveTo(hx + dx * r + r * 0.5, hy + dy * r);
          ctx.arc(hx + dx * r, hy + dy * r, r * 0.5, 0, Math.PI * 2);
        }
      } else {
        // short / buzz / bun: a cap of hair over the top and back
        ctx.arc(hx, hy, r * 1.06, Math.PI * 0.95, Math.PI * 2.08);
        ctx.lineTo(hx - r * 0.3, hy + r * 0.1);
        ctx.closePath();
        if (p.hair === "bun") {
          ctx.moveTo(hx - r * 0.55 + r * 0.55, hy - r * 1.15);
          ctx.arc(hx - r * 0.55, hy - r * 1.15, r * 0.55, 0, Math.PI * 2);
        }
      }
      ctx.globalAlpha = p.hair === "buzz" ? 0.55 : 1;
      ctx.fill();
      ctx.globalAlpha = 1;
    };

    const drawPlayer = (p, x, y, now) => {
      const kit = KITS[p.team], k = 1.45 * s; // ~52px tall on desktop
      const run = Math.min(1, Math.hypot(p.vu, p.vv) / (55 * s));
      const sw = Math.sin(p.phase) * run;
      const kicking = now - p.kickT < 230;
      const bob = Math.abs(Math.cos(p.phase)) * run * 1.6 * k;
      const shirt = p.keeper ? kit.keeper : kit.shirt;

      ctx.save();
      ctx.translate(x, y);
      ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
      ctx.beginPath();
      ctx.ellipse(0, 0, 9 * k, 3 * k, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.scale(Math.sign(p.face) * Math.max(0.2, Math.abs(p.face)), 1); // everything below faces right, then flips
      ctx.translate(0, -bob);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      // a: leg swing, -1 (back) .. 1 (forward); a kick snaps the front leg out
      const leg = (a, shade) => {
        const kneeX = a * 3 * k + 1.2 * k, kneeY = -7.5 * k;
        const footX = a * 6.5 * k, footY = -1.2 * k - Math.max(0, a - 1) * 4 * k;
        limb([0, -13 * k, kneeX, kneeY], p.skin, 3.4 * k, shade);
        limb([kneeX, kneeY, footX, footY], kit.sock, 3.4 * k, shade);
        ctx.fillStyle = "#0a0a0b";
        ctx.beginPath();
        ctx.ellipse(footX + 1.4 * k, footY + 0.2 * k, 2.8 * k, 1.5 * k, 0, 0, Math.PI * 2);
        ctx.fill();
      };
      const arm = (a, shade) => {
        const hand = [a * 4.5 * k + 0.5 * k, -16.5 * k];
        limb([0, -23.5 * k, a * 2.6 * k, -19.5 * k, ...hand], p.skin, 2.7 * k, shade);
        limb([0, -23.5 * k, a * 1.2 * k, -21.5 * k], shirt, 3.6 * k, shade); // sleeve
        if (p.keeper) {
          ctx.fillStyle = kit.gloves;
          ctx.beginPath(); ctx.arc(hand[0], hand[1], 1.6 * k, 0, Math.PI * 2); ctx.fill();
        }
      };

      // standing players keep a little stance so both legs show
      const stance = 0.4 * (1 - run);
      arm(sw * 0.9 - stance * 0.5, 0.28);
      leg(kicking ? -0.4 : -sw - stance, 0.28);
      leg(kicking ? 1.9 : sw + stance, 0);

      // shorts and shirt
      ctx.fillStyle = kit.shorts;
      ctx.beginPath(); ctx.roundRect(-5 * k, -16.5 * k, 10 * k, 5.5 * k, 1.5 * k); ctx.fill();
      // shirt: rounded shoulders tapering to the waist
      ctx.fillStyle = shirt;
      ctx.beginPath();
      ctx.moveTo(-4.2 * k, -15.5 * k);
      ctx.lineTo(-5.2 * k, -23.5 * k);
      ctx.quadraticCurveTo(-5.2 * k, -27 * k, -1.5 * k, -27 * k);
      ctx.lineTo(2 * k, -27 * k);
      ctx.quadraticCurveTo(5.4 * k, -27 * k, 5.2 * k, -23.5 * k);
      ctx.lineTo(4.4 * k, -15.5 * k);
      ctx.closePath();
      ctx.fill();
      ctx.save();
      ctx.clip();
      ctx.fillStyle = kit.trim;
      ctx.fillRect(-5.4 * k, -27 * k, 1.6 * k, 12 * k); // side stripe down the back seam
      ctx.restore();
      limb([-0.8 * k, -26.8 * k, 1.8 * k, -26.8 * k], kit.trim, 1.3 * k, 0); // collar

      arm(-sw * 0.9 + stance * 0.5, 0);

      // head: neck, face, hair, and an eye to show which way they look
      const hx = 0.8 * k, hy = -30.5 * k, hr = 4.3 * k;
      if (p.hair === "afro") drawHair(p, hx, hy, hr);
      limb([0.3 * k, -27 * k, 0.5 * k, -28.5 * k], p.skin, 2.6 * k, 0);
      ctx.fillStyle = p.skin;
      ctx.beginPath(); ctx.arc(hx, hy, hr, 0, Math.PI * 2); ctx.fill();
      if (p.hair !== "afro") drawHair(p, hx, hy, hr);
      ctx.fillStyle = "#0a0a0b";
      ctx.beginPath(); ctx.arc(hx + 2.3 * k, hy + 0.2 * k, 0.65 * k, 0, Math.PI * 2); ctx.fill();

      ctx.restore();
    };

    const drawBall = (x, y) => {
      const r = ball.r, lift = ball.z;
      ctx.fillStyle = `rgba(0, 0, 0, ${0.5 - Math.min(0.3, lift / (120 * s))})`;
      ctx.beginPath();
      ctx.ellipse(x + lift * 0.15, y, r * (1 + lift / (90 * s)), r * 0.45, 0, 0, Math.PI * 2);
      ctx.fill();

      const by = y - r - lift;
      ctx.save();
      ctx.translate(x, by);
      ctx.beginPath();
      ctx.arc(0, 0, r, 0, Math.PI * 2);
      ctx.fillStyle = "#f4f4f1";
      ctx.fill();
      ctx.clip();
      ctx.rotate(ball.spin);
      ctx.fillStyle = "#111";
      ctx.beginPath(); ctx.arc(0, 0, r * 0.38, 0, Math.PI * 2); ctx.fill();
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2;
        ctx.beginPath(); ctx.arc(Math.cos(a) * r * 1.05, Math.sin(a) * r * 1.05, r * 0.36, 0, Math.PI * 2); ctx.fill();
      }
      ctx.restore();
      ctx.lineWidth = 1;
      ctx.strokeStyle = "#111";
      ctx.beginPath(); ctx.arc(x, by, r, 0, Math.PI * 2); ctx.stroke();
    };

    const drawFlash = (now) => {
      const age = (now - flash.t) / 1000;
      if (age > 1.6) { flash = null; return; }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const size = Math.round(56 * s);
      ctx.font = `900 ${size}px Archivo, sans-serif`;
      const text = flash.text || "GOAL!";
      const w = ctx.measureText(text).width + size * 0.5, h = size * 1.1;
      let [x, y] = toScreen(flash.side ? L - 40 * s : 40 * s, B / 2);
      x = Math.max(8, Math.min(W - w - 8, x - w / 2));
      y = Math.max(8, Math.min(H - h - 8, y - h / 2));
      ctx.globalAlpha = Math.min(1, (1.6 - age) * 2.5);
      ctx.translate(x + w / 2, y + h / 2);
      ctx.scale(1 + 0.15 * Math.exp(-age * 6), 1 + 0.15 * Math.exp(-age * 6));
      ctx.fillStyle = "#0a0a0b";
      ctx.fillRect(-w / 2, -h / 2, w, h);
      ctx.fillStyle = flash.color || (flash.side ? "#f5b417" : "#2346ff");
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(text, 0, size * 0.05);
      ctx.globalAlpha = 1;
    };

    const draw = (now) => {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (vertical) ctx.setTransform(0, dpr, dpr, 0, 0, 0);
      else ctx.setTransform(dpr, 0, 0, dpr, dpr * ox, dpr * oy);
      drawPitch();

      // sprites in screen space, back to front so overlaps read right
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      drawLockups(now);
      const sprites = players.filter(playing).map((p) => {
        const [x, y] = toScreen(p.u, p.v);
        return { y, draw: () => drawPlayer(p, x, y, now) };
      });
      if (ball.live) {
        const [x, y] = toScreen(ball.u, ball.v);
        sprites.push({ y, draw: () => drawBall(x, y) });
      }
      sprites.sort((a, b) => a.y - b.y).forEach((sp) => sp.draw());
      if (flash) drawFlash(now);
    };

    // A signup is a goal for everyone
    root.addEventListener("koi:signed-up", () => {
      flash = { side: Math.random() < 0.5 ? 0 : 1, t: performance.now(), text: "YOU'RE IN!", color: "#d7ff2b" };
    });

    // Click shoots (desktop); a tap passes the ball toward the finger (touch)
    root.addEventListener("pointerdown", (e) => {
      if (!ball.live || e.target.closest(".koi-panel, a")) return;
      const [u, v] = toPitch(e.clientX, e.clientY);
      const du = ball.u - u, dv = ball.v - v, d = Math.hypot(du, dv);
      if (e.pointerType === "mouse") {
        if (d < 110 * s) { kickBall(du, dv, 760 * s); interacted(); }
      } else {
        kickBall(-du, -dv, Math.min(Math.max(d * 2, 260 * s), 760 * s));
        interacted();
      }
    });

    resize();
    addEventListener("resize", resize);
    let last = performance.now();
    const frame = (now) => {
      step(Math.max(Math.min((now - last) / 1000, 1 / 30), 1 / 240), now);
      last = now;
      draw(now);
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }

  /* ------------------------------------------------------------ scramble */

  const GLYPHS = "KIDSOFMMGRANT0123456789#%&*+/<>";
  function scramble(el) {
    const final = el.textContent;
    const start = performance.now();
    const step = (now) => {
      const t = now - start;
      let out = "";
      for (let i = 0; i < final.length; i++) {
        const ch = final[i];
        out += ch === " " || t > 220 + i * 45 ? ch : GLYPHS[(Math.random() * GLYPHS.length) | 0];
      }
      el.textContent = out;
      if (out !== final) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  /* ---------------------------------------------------------------- form */

  function startForm() {
    const form = root.querySelector("[data-koi-form]");
    const done = root.querySelector("[data-koi-done]");
    const error = root.querySelector("[data-koi-error]");
    const submit = root.querySelector("[data-koi-submit]");
    const label = submit.textContent;

    const showError = (msg) => { error.textContent = msg; error.hidden = false; };

    const setText = (sel, text) => { done.querySelector(sel).textContent = text; };
    const showDone = (data) => {
      const first = String(data.get("first_name") || "").trim();
      setText("[data-koi-done-name]", first ? `${first},` : "");
      const shoes = done.querySelector("[data-koi-ticket-shoes]");
      shoes.replaceChildren(...data.getAll("shoes").map((name) => Object.assign(document.createElement("b"), { textContent: name })));
      setText("[data-koi-ticket-size]", String(data.get("size") || ""));
      setText("[data-koi-ticket-email]", String(data.get("email") || "").trim());
      form.hidden = true;
      done.hidden = false;
      done.focus();
      root.querySelector(".koi-panel").scrollIntoView({ block: "nearest", behavior: reduceMotion ? "auto" : "smooth" });
      celebrate();
    };

    // confetti in the kit colours, plus a ripple through the background
    const celebrate = () => {
      const r = done.getBoundingClientRect();
      const x = r.left + r.width / 2, y = r.top + Math.min(r.height / 2, 120);
      kicks.push({ x, y, t: performance.now() });
      root.dispatchEvent(new CustomEvent("koi:signed-up"));
      if (reduceMotion) return;
      const colors = ["#f5b417", "#2346ff", "#d7ff2b", "#e2501f", "#f4f4f1"];
      for (let i = 0; i < 48; i++) {
        const bit = document.createElement("i");
        const a = Math.random() * Math.PI * 2, d = 120 + Math.random() * 260;
        bit.className = "koi-confetti";
        bit.style.cssText = `left:${x}px;top:${y}px;background:${colors[i % colors.length]};` +
          `--dx:${Math.cos(a) * d}px;--dy:${Math.sin(a) * d * 0.7 + 160}px;--r:${(Math.random() - 0.5) * 900}deg;` +
          `animation-delay:${Math.random() * 0.12}s`;
        bit.addEventListener("animationend", () => bit.remove());
        root.appendChild(bit);
      }
    };

    // Same computer, next person (events, families): back to a fresh form
    done.querySelector("[data-koi-again]").addEventListener("click", () => {
      form.reset();
      form.querySelectorAll(".is-invalid").forEach((el) => el.classList.remove("is-invalid"));
      error.hidden = true;
      done.hidden = true;
      form.hidden = false;
      form.querySelector("input[name=first_name]").focus();
    });

    // Public key: section setting, else whatever the Klaviyo app embed loaded
    const publicKey = () => {
      if (root.dataset.publicKey) return root.dataset.publicKey;
      for (const s of document.querySelectorAll('script[src*="klaviyo.js"]')) {
        const m = s.src.match(/onsite\/js\/([A-Za-z0-9]{6})\/klaviyo\.js/) || s.src.match(/company_id=([A-Za-z0-9]{6})/);
        if (m) return m[1];
      }
      return "";
    };

    form.addEventListener("input", (e) => {
      e.target.closest(".koi-field")?.classList.remove("is-invalid");
      error.hidden = true;
    });

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      error.hidden = true;
      const data = new FormData(form);

      // Bots fill the hidden field; pretend it worked
      if (data.get("website")) { showDone(data); return; }

      const invalid = [...form.querySelectorAll("input[required], select[required]")].filter((el) => !el.checkValidity());
      // shoes: at least one of the boxes
      const shoeBoxes = [...form.querySelectorAll('input[name="shoes"]')];
      if (shoeBoxes.length && !shoeBoxes.some((el) => el.checked)) invalid.push(shoeBoxes[0]);
      invalid.forEach((el) => el.closest(".koi-field")?.classList.add("is-invalid"));
      if (invalid.length) {
        invalid[0].focus();
        showError(
          invalid.some((el) => el.type === "email" && el.value) ? "That email doesn't look right."
            : invalid.length === 1 && invalid[0].name === "shoes" ? "Pick at least one shoe."
            : "Fill in the highlighted fields."
        );
        return;
      }

      const key = publicKey();
      if (!key || !root.dataset.listId) {
        showError("Sign up is unavailable right now. Please try again later.");
        console.warn("[koi] missing Klaviyo public key or list id");
        return;
      }

      const str = (k) => String(data.get(k) || "").trim();
      const attributes = {
        email: str("email"),
        first_name: str("first_name"),
        properties: { koi_shoes: data.getAll("shoes"), koi_shoe_size: str("size"), koi_signup_page: location.pathname },
      };
      // No "subscriptions" field: the client subscriptions endpoint rejects it and
      // records email marketing consent for the list on its own.
      if (str("last_name")) attributes.last_name = str("last_name");
      if (str("country")) attributes.location = { country: str("country") };

      submit.disabled = true;
      submit.textContent = "SENDING…";
      try {
        const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(key)}`, {
          method: "POST",
          headers: { "Content-Type": "application/vnd.api+json", revision: "2024-10-15" },
          body: JSON.stringify({
            data: {
              type: "subscription",
              attributes: {
                custom_source: root.dataset.source || "KOI Signup Page",
                profile: { data: { type: "profile", attributes } },
              },
              relationships: { list: { data: { type: "list", id: root.dataset.listId } } },
            },
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          const detail = body?.errors?.[0]?.detail || "";
          console.warn("[koi] Klaviyo rejected the signup:", res.status, detail);
          throw new Error(/email/i.test(detail) ? "That email doesn't look right." : "Something went wrong. Please try again.");
        }
        showDone(data);
      } catch (err) {
        showError(err.message && !/fetch/i.test(err.message) ? err.message : "Network error. Please try again.");
      } finally {
        submit.disabled = false;
        submit.textContent = label;
      }
    });
  }

  /* ---------------------------------------------------------------- wire */

  addEventListener("pointermove", (e) => {
    pointer.x = e.clientX;
    pointer.y = e.clientY;
    pointer.active = true;
  }, { passive: true });
  document.addEventListener("pointerleave", () => { pointer.active = false; });
  addEventListener("pointerdown", (e) => {
    pointer.x = e.clientX;
    pointer.y = e.clientY;
    kicks.push({ x: e.clientX, y: e.clientY, t: performance.now() });
  }, { passive: true });

  startBackground();
  startBall();
  startGame();
  startForm();
  if (!reduceMotion) root.querySelectorAll("[data-koi-scramble]").forEach(scramble);
})();
